import { useCallback } from 'react';
import { setTokens, getUserDataFromToken, saveUserData, type UserData } from '../../services/auth';
import { setSession } from '../../services/auth';

const AUTH_URL = process.env.NEXT_PUBLIC_BASE_URL || '';

interface UseOAuthActionsProps {
  setUser: (user: UserData | null) => void;
  setLoading: (loading: boolean) => void;
  clearAuthData: () => void;
}

export function useOAuthActions({ setUser, setLoading, clearAuthData }: UseOAuthActionsProps) {
  const processOAuthTokens = useCallback(
    async (hash: string): Promise<boolean> => {
      setLoading(true);
      try {
        const params = new URLSearchParams(hash.substring(1));
        const accessToken = params.get('access_token');
        const refreshToken = params.get('refresh_token');

        if (!accessToken) {
          throw new Error('No access token found in OAuth hash.');
        }

        setTokens(accessToken, refreshToken || undefined);
        await setSession(accessToken, refreshToken || undefined);

        let userData = getUserDataFromToken(accessToken);

        try {
          const response = await fetch(`${AUTH_URL}/api/auth/user`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });

          if (response.ok) {
            const data = await response.json();

            if (userData && data?.created_at) {
              userData.joiningDate = data.created_at;
              userData.lastSignInAt = data.last_sign_in_at;
              userData.emailConfirmedAt = data.email_confirmed_at;

              const apiProfile = data.user_metadata?.profile || {};
              if (Object.keys(apiProfile).length > 0) {
                userData.profile = {
                  country: apiProfile.country || '',
                  city: apiProfile.city || '',
                  zipCode: apiProfile.zipCode || '',
                  bio: apiProfile.bio || '',
                  profileImage: apiProfile.profileImage || '',
                  coverImage: apiProfile.coverImage || '',
                };
              }
            }
          }
        } catch (apiError) {
          console.warn('Error fetching user data from API after OAuth:', apiError);
        }

        if (userData) saveUserData(userData);
        setUser(userData);

        return true;
      } catch (error) {
        console.error('Error processing OAuth tokens:', error);
        clearAuthData();
        return false;
      } finally {
        setLoading(false);
      }
    },
    [setUser, setLoading, clearAuthData]
  );

  return { processOAuthTokens };
}
